/**
 * Checkout + Stripe Checkout Session hooks.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { clearEmbedHandover } from "@/embed/surface";
import { apiFetch } from "@/lib/api";
import { clearCheckoutDraft } from "@/lib/checkoutDraft";
import { clearTrialToken, currentTrialToken } from "@/lib/storefrontTrial";
import type {
  CheckoutDefaultCountry,
  CheckoutPrefill,
  CheckoutRequest,
  CheckoutResponse,
} from "@/api/types";

import { queryKeys } from "./keys";

const API = "/api/v1";

// --- Checkout ---------------------------------------------------------

export function useCheckout(
  options?: Omit<
    UseMutationOptions<CheckoutResponse, Error, CheckoutRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<CheckoutResponse, Error, CheckoutRequest>({
    mutationFn: async (payload) => {
      // KAN-761 — pozvánka „vyzkoušej si svůj obchod" se přikládá tady,
      // ne v každé pokladně zvlášť: je to vlastnost prohlížeče, ne
      // formuláře, a jediné místo, kudy objednávka odchází, je tohle.
      const trialToken = currentTrialToken();
      const res = await apiFetch<CheckoutResponse>(`${API}/checkout`, {
        method: "POST",
        body: trialToken ? { ...payload, trial_token: trialToken } : payload,
      });
      // Pozvánka je jednorázová a účel splnila. Bez smazání by tenhle
      // prohlížeč označoval jako testovací i objednávky, které tenant
      // po rozjezdu udělá doopravdy.
      clearTrialToken();
      // The embed journey ends here. Cleared inside the mutation rather
      // than in ``onSuccess`` because a caller passing its own
      // ``onSuccess`` replaces this one — and a marker left behind
      // would credit the embed with every later order from this
      // browser, including ones that started on the shop's homepage.
      clearEmbedHandover();
      // KAN-2093 — rozpracovaný formulář patří k nákupu, který právě
      // skončil. Maže se tady, ne až v ``onSuccess`` volajícího: kdyby
      // ho volající vynechal (nebo nahradil), další nákup v téže záložce
      // by zdědil firmu, IČO i telefon z minulé objednávky.
      clearCheckoutDraft();
      return res;
    },
    ...options,
    onSuccess: (...args) => {
      const [res] = args;
      // KAN-2093 — úklid běží VŽDY, i když volající předá vlastní
      // ``onSuccess``. Dřív ho rozprostřené ``...options`` přepsalo
      // (pokladna svůj ``onSuccess`` předává), košík se po objednávce
      // nikdy nezneplatnil a další nákup ukazoval souhrn minulého.
      resetShopperStateAfterOrder(qc);
      qc.invalidateQueries({ queryKey: queryKeys.order(res.order_number) });
      return options?.onSuccess?.(...args);
    },
  });
}

/**
 * KAN-2093 — po odeslané objednávce zahoď všechno, co prohlížeč drží
 * o právě dokončeném nákupu: košík a vše z něj odvozené (souhrn
 * pokladny, příplatky úrovní termínu, odhad dopravy), nabídku dopravců
 * a dostupnost dopravy (počítané z hmotnosti TOHO košíku), předvyplnění
 * formuláře i koncept formuláře.
 *
 * ``removeQueries``, ne ``invalidateQueries``: zneplatněný dotaz dál
 * VRACÍ stará data, dokud nedoběhne nový fetch — a přesně ta stará data
 * zákazník viděl (mezisoučet minulé objednávky, příplatek za termín
 * spočítaný z minulého košíku). Odstraněný dotaz začíná od stavu
 * „načítá se" a obrazovky ukážou načítání místo cizích čísel.
 */
export function resetShopperStateAfterOrder(qc: QueryClient): void {
  clearCheckoutDraft();
  qc.removeQueries({ queryKey: queryKeys.cart() });
  qc.removeQueries({ queryKey: queryKeys.cartPriceListChange() });
  qc.removeQueries({ queryKey: ["checkout"] });
}

// --- Ukázkový obchod platformy (KAN-35) --------------------------------
//
// Na obchodě s ``is_platform_demo`` „Zaplatit" objednávku NEZAKLÁDÁ:
// server jen zapíše CRM signál (``demo_order``) a pokladna přesměruje
// na ``/demo-shop``. Košík zůstává — proto se tu nic neinvaliduje.

export function useDemoCheckoutEnd(
  options?: Omit<UseMutationOptions<void, Error, void>, "mutationFn">,
) {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiFetch<void>(`${API}/checkout/demo-end`, { method: "POST" });
    },
    ...options,
  });
}

// --- Prefill (KAN-923) -------------------------------------------------
//
// Address book default → last order in this shop → profile. Only for a
// signed-in customer: a guest has no history and the BE answers 401,
// so callers pass ``enabled: isLoggedIn``. KAN-2111 — checkout of an issued
// quote (``quoteCheckout``) prefills the quote's addressee, guest included;
// the header travels with ``apiFetch``, the key keeps the two apart.

export function useCheckoutPrefill(
  options?: Omit<UseQueryOptions<CheckoutPrefill>, "queryKey" | "queryFn">,
  quoteCheckout?: string | null,
) {
  return useQuery<CheckoutPrefill>({
    queryKey: ["checkout", "prefill", quoteCheckout ?? null],
    queryFn: () => apiFetch<CheckoutPrefill>(`${API}/checkout/prefill`),
    staleTime: 60 * 1000,
    retry: false,
    ...options,
  });
}

// --- Výchozí země (KAN-1928) -------------------------------------------
//
// Země zákazníka podle IP, pokud tam obchod doručuje, jinak první
// doručovací země. Jen výchozí bod: prefill z minulé objednávky a ruční
// volba ji přebijí (rozhoduje ``Checkout.tsx``).

export function useCheckoutDefaultCountry() {
  return useQuery<CheckoutDefaultCountry>({
    queryKey: ["checkout", "defaultCountry"],
    queryFn: () =>
      apiFetch<CheckoutDefaultCountry>(`${API}/checkout/default-country`),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** KAN-993 — čím smí TENHLE návštěvník u tohoto obchodu zaplatit. */
export interface CheckoutPaymentMethods {
  /** V pořadí, ve kterém je pokladna nabídne; první je předvybraný. */
  methods: ("card" | "bank_transfer" | "account")[];
}

/**
 * Nabídka způsobů platby pro tohohle zákazníka (KAN-993).
 *
 * Vlastní dotaz, a ne pole v brandingu, protože odpověď závisí na TOM,
 * KDO se ptá: „převodem na fakturu" se podle nastavení tenanta nabízí
 * jen členovi firemního účtu, nebo jen přihlášenému. Branding je
 * anonymní a cachovaný napříč návštěvníky, tohle ne — proto i kratší
 * ``staleTime`` a klíč, který se invaliduje s přihlášením.
 *
 * Anonymní volající je v pořádku: pokladna běží i bez přihlášení.
 */
export function useCheckoutPaymentMethods(
  options?: Omit<UseQueryOptions<CheckoutPaymentMethods>, "queryKey" | "queryFn">,
) {
  return useQuery<CheckoutPaymentMethods>({
    queryKey: ["checkout", "payment-methods"],
    queryFn: () =>
      apiFetch<CheckoutPaymentMethods>(`${API}/checkout/payment-methods`),
    staleTime: 30 * 1000,
    ...options,
  });
}

// --- Stripe Checkout Session -----------------------------------------

interface StripeSessionResponse {
  session_id: string;
  url: string;
}

export function useCreateStripeSession(
  options?: Omit<
    UseMutationOptions<StripeSessionResponse, Error, string>,
    "mutationFn"
  >,
) {
  return useMutation<StripeSessionResponse, Error, string>({
    mutationFn: (orderNumber) =>
      apiFetch<StripeSessionResponse>(
        `${API}/checkout/${orderNumber}/stripe-session`,
        { method: "POST" },
      ),
    ...options,
  });
}
